import { useEffect, useState } from 'react';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { IncomeType } from '../hooks/useIncomes';
import { incomeTypeOptions } from '../incomeTypes';
import { conversionPreviewNote } from '../leftOut';
import { moneyOrNotRecorded } from '../notRecorded';
import { IncomeRow } from '../rows';
import {
  CashflowFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
  cashflowFrequencyOptions,
} from '../utils';
import { CashflowModalShell } from './modal/CashflowModalShell';
import { deletionMessage } from './modal/deletion';
import { AmountField, allocationsComplete, sumAmounts } from './modal/MemberAllocations';
import { RemovedItem, useAllocations } from './modal/useAllocations';
import { SHARED_ITEM_NOTICE, isPartOfAGroup } from './modal/sharedItem';

const AMOUNT_FIELDS: AmountField[] = [
  { key: 'amountPreTax', label: 'Amount before tax' },
  { key: 'amountPostTax', label: 'Amount after tax' },
];

export type IncomeSubmission = {
  incomeType: IncomeType;
  frequency: CashflowFrequency;
  description: string;
  allocations: {
    clientId: string;
    itemId?: string;
    amountPreTax: number | null;
    amountPostTax: number | null;
  }[];
  groupId?: string;
  removedItemIds: RemovedItem[];
};

type IncomeModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: IncomeSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  initialRow?: IncomeRow;
  /**
   * The view behind this modal holds only one client's rows of a shared item, or cannot vouch
   * for having the rest, so the item is open in part. What every row of the group carries stays
   * read-only — see {@link SHARED_ITEM_NOTICE} — and Delete is withheld, since it would take
   * the rows in hand while the warning named only the members among them.
   */
  ownRowsOnly?: boolean;
  /** Absent where the view cannot delete; the form only offers it on an item that exists. */
  onDelete?: (items: { id: string; clientId: string }[]) => Promise<void>;
};

export function IncomeFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  initialRow,
  ownRowsOnly,
  onDelete,
}: IncomeModalProps) {
  const currency = useCurrency();
  const [incomeType, setIncomeType] = useState<IncomeType>('Employment');
  const [frequency, setFrequency] = useState<CashflowFrequency | ''>('Monthly');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const seed = initialRow?.items.map(item => ({
    clientId: item.clientId,
    itemId: item.id,
    amounts: { amountPreTax: item.amountPreTax, amountPostTax: item.amountPostTax },
  }));
  const { allocations, removedItems, addMember, removeMember, setAmount } = useAllocations(
    opened,
    seed,
    defaultClientId,
  );

  useEffect(() => {
    if (!opened) return;
    setIncomeType(initialRow?.incomeType ?? 'Employment');
    // Detected records can arrive without a frequency, so leave it for the adviser to choose
    setFrequency(initialRow ? (initialRow.frequency ?? '') : 'Monthly');
    setDescription(initialRow?.description ?? '');
  }, [opened, initialRow]);

  const locked = Boolean(ownRowsOnly) && isPartOfAGroup(initialRow?.items);
  const chosenFrequency = frequency || null;
  const totalPreTax = sumAmounts(allocations, 'amountPreTax');
  const totalPostTax = sumAmounts(allocations, 'amountPostTax');
  const canSubmit = !!frequency && allocationsComplete(allocations, AMOUNT_FIELDS);

  const handleSubmit = async () => {
    if (!canSubmit || isSaving || !frequency) return;
    setIsSaving(true);
    try {
      await onSubmit({
        incomeType,
        frequency,
        description,
        groupId: initialRow?.items.find(item => item.groupId)?.groupId ?? undefined,
        allocations: allocations.map(allocation => ({
          clientId: allocation.clientId,
          itemId: allocation.itemId,
          amountPreTax: recordedAmount(allocation.amounts.amountPreTax),
          amountPostTax: recordedAmount(allocation.amounts.amountPostTax),
        })),
        removedItemIds: removedItems,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit income' : 'Add income'}
      submitLabel={initialRow ? 'Save income' : 'Add income'}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      isSaving={isSaving}
      previewTitle="Total income preview"
      previewNote={conversionPreviewNote(chosenFrequency)}
      previewRows={[
        {
          label: 'Before tax',
          qualifier: '(monthly)',
          value: moneyOrNotRecorded(calculateMonthlyAmount(totalPreTax, chosenFrequency), currency),
        },
        {
          label: 'Before tax',
          qualifier: '(annually)',
          value: moneyOrNotRecorded(calculateAnnualAmount(totalPreTax, chosenFrequency), currency),
        },
        {
          label: 'After tax',
          qualifier: '(monthly)',
          value: moneyOrNotRecorded(
            calculateMonthlyAmount(totalPostTax, chosenFrequency),
            currency,
          ),
        },
        {
          label: 'After tax',
          qualifier: '(annually)',
          value: moneyOrNotRecorded(calculateAnnualAmount(totalPostTax, chosenFrequency), currency),
        },
      ]}
      testId="add-income-modal"
      fields={[
        [
          {
            kind: 'select',
            label: 'Type',
            value: incomeType,
            onChange: value => setIncomeType(value as IncomeType),
            data: incomeTypeOptions,
            required: true,
            disabled: locked,
          },
          {
            kind: 'select',
            label: 'Frequency',
            value: frequency,
            onChange: value => setFrequency(value as CashflowFrequency),
            data: cashflowFrequencyOptions,
            required: true,
            disabled: locked,
            error: frequency ? undefined : 'Choose a frequency',
          },
        ],
        [
          {
            kind: 'text',
            label: 'Description',
            value: description,
            onChange: setDescription,
            disabled: locked,
          },
        ],
      ]}
      allocation={{
        members,
        allocations,
        amountFields: AMOUNT_FIELDS,
        amountQualifier: frequency.toLowerCase(),
        emptyLabel: 'Select at least 1 family member to add income',
        membersLocked: locked,
        onAdd: addMember,
        onRemove: removeMember,
        onAmountChange: setAmount,
      }}
      notice={locked ? SHARED_ITEM_NOTICE : undefined}
      deletion={
        initialRow && onDelete && !locked
          ? {
              onDelete: () => onDelete(initialRow.items),
              title: 'Delete income?',
              message: deletionMessage('income', initialRow.members),
              confirmLabel: 'Delete income',
            }
          : undefined
      }
    />
  );
}
